import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import { sortRelations, relationColor } from './trustShared';
import { Explain } from './Explain';
import ArrangeBar from './ArrangeBar';
import { wvConfirm } from './Confirm';

// A viewset's DEFINITION, rendered inline on the merged Home (owner ruling
// 2026-08-05: "every view is a viewset + a ranking rule" arrives at the front
// door; the editor stays on the page — "simple machinery, not complicated
// machinery"). The chips/editor split is unchanged and load-bearing: chips
// NARROW YOUR VIEW; the ⚙ panel changes what the viewset IS — what anyone it
// is shared with sees.
//
// ── WHAT THIS STOPPED OWNING, 2026-08-07 (HANDOFF 0c) ───────────────────────
// It used to fetch its own feed, batch its own lens scores and engagement, and
// rank the result client-side with treeArrange's arrangeObjects. All of that is
// gone: the parent now reads /api/stream, which applies the same filter and
// ranks server-side. The reason is not tidiness — it is that the client-side
// `contested` and /resolve's `contested` were computing different quantities
// (raw lens spread versus the widest NAMED tension), and a pill row that
// switches between those two surfaces cannot carry two meanings of one word.
// backend/lib/streamRank.ts has the full argument.
//
// So this component is now the definition half only: the filter chips (whose
// exclusions the PARENT owns, because they are a query parameter of the list it
// fetches) and the ⚙ editor.
//
// ── WHAT IT GAINED, 2026-08-08 (viewsets into the graph) ────────────────────
// RENAME, DELETE and TAGS. All three land in the ⚙ fold rather than anywhere
// else, by owner ruling the same evening: *"let's have one surface for managing
// viewsets as the MVP."* So there is deliberately no delete on the pill row, no
// rename-in-place on the pill, and no "add to viewset" gesture out on object
// pages — one place where a viewset is managed, and it is this panel.
//
// Rename mattered more than its size suggests. `PATCH /api/viewsets/:id` always
// accepted `name`; this component only ever sent `is_public`, so a viewset built
// mid-reading was named before you knew what it was and then named that forever.

function ViewsetInline({
  id,
  sorts,
  sort,
  onSort,
  excludedFilters,
  onToggleFilter,
  onDefinitionChanged,
  onDeleted,
}) {
  const { user } = useAuth();

  const [viewset, setViewset] = useState(null);
  const [filters, setFilters] = useState([]);
  const [access, setAccess] = useState([]);
  const [compositions, setCompositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  // Filter editor state — tag quick-path first, generic editor behind "advanced"
  const [allRelations, setAllRelations] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [tagResults, setTagResults] = useState([]);
  const [filterRelationId, setFilterRelationId] = useState('');
  const [filterTargetSearch, setFilterTargetSearch] = useState('');
  const [filterTargetId, setFilterTargetId] = useState('');
  const [filterTargetContent, setFilterTargetContent] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [addingFilter, setAddingFilter] = useState(false);

  const [granteeSearch, setGranteeSearch] = useState('');
  const [granteeResults, setGranteeResults] = useState([]);
  const [allViewsets, setAllViewsets] = useState([]);

  // Rename + tags — the management half, added 2026-08-08.
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const isOwner = user && viewset && viewset.creator === user.id;

  // A freshly created viewset opens with its editor unfolded — an empty feed
  // with a closed editor would read as broken rather than as new.
  useEffect(() => {
    setEditing(false);
    setLoading(true);
  }, [id]);

  const fetchViewset = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/viewsets/${id}`);
      if (!res.ok) throw new Error('Viewset not found');
      const data = await res.json();
      setViewset(data);
      setFilters(data.filters || []);
      setAccess(data.access || []);
      setCompositions(data.compositions || []);
      setTags(data.tags || []);
      setNameDraft(data.name || '');
      setError(null);
      if ((data.filters || []).length === 0 && (data.compositions || []).length === 0) {
        setEditing(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchViewset();
    apiFetch('/api/relations').then((r) => r.json()).then(setAllRelations).catch(() => {});
    // `.viewsets`, not the bare array: the list endpoint ships its rule set
    // alongside the rows since 2026-08-08, on the /api/stream contract.
    apiFetch('/api/viewsets')
      .then((r) => r.json())
      .then((d) => setAllViewsets(Array.isArray(d?.viewsets) ? d.viewsets : []))
      .catch(() => {});
  }, [fetchViewset]);

  // Every write below refetches the definition AND tells the parent, whose
  // list is derived from it. Refetching only here would leave the chips
  // describing a filter set the rows no longer reflect.
  const refresh = useCallback(() => {
    fetchViewset();
    if (onDefinitionChanged) onDefinitionChanged();
  }, [fetchViewset, onDefinitionChanged]);

  useEffect(() => {
    if (!tagSearch.trim()) { setTagResults([]); return; }
    const t = setTimeout(() => {
      apiFetch(`/api/objects?object_type=tag&q=${encodeURIComponent(tagSearch.trim())}`)
        .then((r) => r.json())
        .then(setTagResults)
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [tagSearch]);

  useEffect(() => {
    if (!filterTargetSearch.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      apiFetch(`/api/objects/search?q=${encodeURIComponent(filterTargetSearch)}`)
        .then((r) => r.json())
        .then(setSearchResults)
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [filterTargetSearch]);

  // user/group search for access grants — the type-specific listing endpoint,
  // because /api/objects/search excludes user and group types
  useEffect(() => {
    if (!granteeSearch.trim()) { setGranteeResults([]); return; }
    const t = setTimeout(() => {
      const q = encodeURIComponent(granteeSearch.trim());
      Promise.all([
        apiFetch(`/api/objects?object_type=user&q=${q}`).then((r) => r.json()),
        apiFetch(`/api/objects?object_type=group&q=${q}`).then((r) => r.json()),
      ])
        .then(([users, groups]) => setGranteeResults([...users, ...groups]))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [granteeSearch]);

  const taggedRelation = allRelations.find((r) => r.name === 'tagged');

  const handleAddTagFilter = async (tag) => {
    if (!taggedRelation) return;
    try {
      const res = await apiFetch(`/api/viewsets/${id}/filters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relation_id: taggedRelation.id, target_object_id: tag.id }),
      });
      if (!res.ok) throw new Error();
      setTagSearch('');
      setTagResults([]);
      refresh();
    } catch {
      setError('Failed to add tag filter');
    }
  };

  const handleAddFilter = async (e) => {
    e.preventDefault();
    if (!filterRelationId) return;
    try {
      const res = await apiFetch(`/api/viewsets/${id}/filters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          relation_id: filterRelationId,
          target_object_id: filterTargetId || null,
        }),
      });
      if (!res.ok) throw new Error();
      setFilterRelationId('');
      setFilterTargetId('');
      setFilterTargetContent('');
      setFilterTargetSearch('');
      setAddingFilter(false);
      refresh();
    } catch {
      setError('Failed to add filter');
    }
  };

  // Toggle rollup IN PLACE via PATCH, never delete-then-recreate: the filter id
  // is what the ArrangeBar chips carry in `?exclude=`, so a new id would leave a
  // viewer's narrowing pointed at a row that no longer exists and quietly widen
  // their view.
  const handleToggleRollup = async (filterId, next) => {
    // optimistic, because the checkbox is the feedback
    setFilters((prev) => prev.map((f) => (f.id === filterId ? { ...f, include_descendants: next } : f)));
    try {
      const res = await apiFetch(`/api/viewsets/${id}/filters/${filterId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ include_descendants: next }),
      });
      if (!res.ok) throw new Error();
      refresh();
    } catch {
      setFilters((prev) => prev.map((f) => (f.id === filterId ? { ...f, include_descendants: !next } : f)));
      setError('Failed to change the filter');
    }
  };

  const handleRemoveFilter = async (filterId) => {
    try {
      await apiFetch(`/api/viewsets/${id}/filters/${filterId}`, { method: 'DELETE' });
      refresh();
    } catch {
      setError('Failed to remove filter');
    }
  };

  const handleTogglePublic = async () => {
    try {
      const res = await apiFetch(`/api/viewsets/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_public: !viewset.is_public }),
      });
      if (!res.ok) throw new Error();
      refresh();
    } catch {
      setError('Failed to update viewset');
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    const next = nameDraft.trim();
    if (!next || next === viewset.name) { setRenaming(false); return; }
    try {
      const res = await apiFetch(`/api/viewsets/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: next }),
      });
      if (!res.ok) throw new Error();
      setRenaming(false);
      refresh();
    } catch {
      setError('Failed to rename viewset');
    }
  };

  // Deleting is SOFT server-side, so the copy says "remove" rather than
  // promising destruction the backend deliberately does not perform. The
  // definition survives and could be restored; nothing in the app offers that
  // yet, which is why the wording does not promise it either.
  const handleDelete = async () => {
    if (!(await wvConfirm(
      `Remove the viewset “${viewset.name}”? Its filters and sharing are kept, but it leaves your list.`,
      { confirmLabel: 'Remove' }
    ))) return;
    try {
      const res = await apiFetch(`/api/viewsets/${id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error();
      if (onDeleted) onDeleted(id);
    } catch {
      setError('Failed to remove viewset');
    }
  };

  // One field for both "attach an existing tag" and "make a new one" — the
  // server's ensureTag decides which happened. Before this, the filter editor
  // could only offer tags that already existed, so organizing around a topic
  // you had just started reading about meant leaving to mint the tag first.
  const handleAddTag = async (e) => {
    e.preventDefault();
    const name = tagDraft.trim();
    if (!name) return;
    try {
      const res = await apiFetch(`/api/viewsets/${id}/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: name }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to tag viewset'); return; }
      setTagDraft('');
      refresh();
    } catch {
      setError('Failed to tag viewset');
    }
  };

  const handleRemoveTag = async (edgeId) => {
    try {
      await apiFetch(`/api/viewsets/${id}/tags/${edgeId}`, { method: 'DELETE' });
      refresh();
    } catch {
      setError('Failed to remove tag');
    }
  };

  const handleGrantAccess = async (granteeObjectId) => {
    try {
      await apiFetch(`/api/viewsets/${id}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grantee_object_id: granteeObjectId }),
      });
      setGranteeSearch('');
      setGranteeResults([]);
      refresh();
    } catch {
      setError('Failed to grant access');
    }
  };

  const handleRevokeAccess = async (accessId) => {
    try {
      await apiFetch(`/api/viewsets/${id}/access/${accessId}`, { method: 'DELETE' });
      refresh();
    } catch {
      setError('Failed to revoke access');
    }
  };

  const handleAddComposition = async (childId) => {
    try {
      const res = await apiFetch(`/api/viewsets/${id}/compositions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ child_viewset_id: childId }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      refresh();
    } catch {
      setError('Failed to add composition');
    }
  };

  const handleRemoveComposition = async (compositionId) => {
    try {
      await apiFetch(`/api/viewsets/${id}/compositions/${compositionId}`, { method: 'DELETE' });
      refresh();
    } catch {
      setError('Failed to remove composition');
    }
  };

  const filterGroups = filters.reduce((acc, f) => {
    const key = f.relation_name;
    if (!acc[key]) acc[key] = [];
    acc[key].push(f);
    return acc;
  }, {});

  if (loading) return <p>Loading…</p>;
  if (error && !viewset) return <p style={{ color: 'var(--wv-neg)' }}>{error}</p>;

  const availableViewsets = allViewsets.filter(
    (vs) => vs.id !== id && !compositions.find((c) => c.child_viewset_id === vs.id)
  );

  const removeBtnStyle = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--wv-muted)', fontSize: '14px', padding: '0 2px',
  };
  const rowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '4px 8px', backgroundColor: 'var(--wv-surface2)',
    borderRadius: 'var(--wv-r-xs)', marginBottom: '3px', fontSize: 'var(--wv-fs-md)',
  };

  return (
    <div>
      {error && <div style={{ color: 'var(--wv-neg)', marginBottom: 'var(--wv-sp-5)' }}>{error}</div>}

      {/* control row: the shared two-tier arrangement + the ⚙ fold.
          The old `needs judgment →` link out to /resolve is GONE and its
          absence is the point of the fold: that page was this viewset under
          the `contested` rule, which is now one of the pills to the left. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--wv-sp-3)' }}>
        <div style={{ flex: 1 }}>
          <ArrangeBar
            sorts={sorts}
            sort={sort}
            onSort={onSort}
            filterAria="narrow this view"
            filterItems={(filters || []).map((f) => {
              const on = !excludedFilters.has(f.id);
              const groupOn = (filters || []).filter(
                (g) => g.relation_id === f.relation_id && !excludedFilters.has(g.id)
              ).length;
              return {
                key: f.id,
                label: f.target_content
                  ? f.relation_name === 'tagged'
                    ? f.target_content
                    : `${f.relation_name.replace(/_/g, ' ')}: ${f.target_content.substring(0, 24)}${f.target_content.length > 24 ? '…' : ''}`
                  : `any ${f.relation_name.replace(/_/g, ' ')}`,
                on,
                color: f.relation_name === 'tagged' ? 'var(--wv-accent)' : relationColor(f.relation_name),
                disabled: on && groupOn === 1,
                disabledHint:
                  'the last filter in its group — removing it would widen the view, not narrow it',
              };
            })}
            onToggleFilter={onToggleFilter}
          />
        </div>
        <div style={{ display: 'flex', gap: 'var(--wv-sp-2)', alignItems: 'center', paddingTop: 'var(--wv-sp-2)', flexShrink: 0 }}>
          <button
            onClick={() => setEditing(!editing)}
            aria-expanded={editing}
            className={editing ? 'wv-tint' : undefined}
            style={{
              background: editing ? undefined : 'none',
              border: '1px solid var(--wv-border)',
              borderRadius: 'var(--wv-r-pill)',
              color: editing ? 'var(--wv-accent)' : 'var(--wv-muted)',
              fontSize: 'var(--wv-fs-xs)',
              padding: '2px 9px',
              lineHeight: 1.6,
              cursor: 'pointer',
            }}
          >
            ⚙ edit
          </button>
        </div>
      </div>

      {/* ── the definition editor: the ⚙ fold ── */}
      {editing && (
        <div
          className="wv-panel"
          style={{ marginBottom: 'var(--wv-sp-6)', border: '1px solid var(--wv-border-strong)' }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--wv-sp-4)', marginBottom: 'var(--wv-sp-4)', flexWrap: 'wrap' }}>
            {renaming && isOwner ? (
              <form onSubmit={handleRename} style={{ display: 'flex', gap: 'var(--wv-sp-2)', alignItems: 'center' }}>
                <input
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setNameDraft(viewset.name); setRenaming(false); }
                  }}
                  autoFocus
                  style={{ fontSize: 'var(--wv-fs-ui)', minWidth: '200px' }}
                  aria-label="viewset name"
                />
                <button type="submit" disabled={!nameDraft.trim()} style={{ fontSize: 'var(--wv-fs-xs)' }}>save</button>
                <button
                  type="button"
                  onClick={() => { setNameDraft(viewset.name); setRenaming(false); }}
                  style={{ ...removeBtnStyle, fontSize: 'var(--wv-fs-xs)' }}
                >
                  cancel
                </button>
              </form>
            ) : (
              <strong
                style={{ fontSize: 'var(--wv-fs-ui)', cursor: isOwner ? 'text' : 'default' }}
                onClick={() => isOwner && setRenaming(true)}
                title={isOwner ? 'click to rename' : undefined}
              >
                {viewset.name}
              </strong>
            )}
            <span style={{ fontSize: 'var(--wv-fs-sm)', color: 'var(--wv-muted)' }}>
              {viewset.is_public ? 'public' : 'private'} · by {viewset.creator_username}
            </span>
            <span style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)', marginLeft: 'auto' }}>
              chips above narrow your view — this panel changes what the viewset is
            </span>
          </div>

          {/* ── TAGS: the organizing answer (owner ask 2026-08-08, "a way to
              organize viewsets since I expect my list will soon overflow").
              This is also the door that MINTS the viewset's graph handle,
              lazily and on first use — before a tag exists there is nothing in
              the graph pointing at this viewset and no object for it to be. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-2)', flexWrap: 'wrap', marginBottom: 'var(--wv-sp-4)' }}>
            {tags.map((t) => (
              <span key={t.edge_id} className="wv-chip" style={{ color: 'var(--wv-accent)' }}>
                {t.content}
                {isOwner && (
                  <button
                    onClick={() => handleRemoveTag(t.edge_id)}
                    style={{ ...removeBtnStyle, marginLeft: '4px' }}
                    aria-label={`remove tag ${t.content}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {isOwner && (
              <form onSubmit={handleAddTag} style={{ display: 'inline-flex', gap: 'var(--wv-sp-2)' }}>
                <input
                  type="text"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  placeholder={tags.length ? '+ tag' : 'tag this viewset…'}
                  title="Group your viewsets. A name that does not exist yet is created."
                  style={{ fontSize: 'var(--wv-fs-xs)', width: '130px' }}
                />
                {tagDraft.trim() && (
                  <button type="submit" style={{ fontSize: 'var(--wv-fs-xs)' }}>add</button>
                )}
              </form>
            )}
          </div>

          <div className="vs-split">
            {/* Filters */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--wv-fs-ui)' }}><Explain concept="viewset-filters">Filters</Explain></h3>
                {isOwner && (
                  <button onClick={() => setAddingFilter(!addingFilter)} style={{ fontSize: '12px' }}>
                    {addingFilter ? 'hide advanced' : 'advanced'}
                  </button>
                )}
              </div>

              {isOwner && (
                <div style={{ position: 'relative', marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Add a tag filter…"
                    title={taggedRelation ? 'Objects tagged with this will appear in the feed' : 'No tagged relation exists yet'}
                    disabled={!taggedRelation}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: '13px' }}
                  />
                  {tagResults.length > 0 && (
                    <div className="wv-dropdown">
                      {tagResults.slice(0, 8).map((tag) => (
                        <div key={tag.id} className="wv-dropdown-item" onClick={() => handleAddTagFilter(tag)}>
                          {tag.content}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {addingFilter && (
                <form onSubmit={handleAddFilter} style={{ marginBottom: '12px', padding: '10px', border: '1px solid var(--wv-border)', fontSize: '13px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ display: 'block', marginBottom: '4px' }}>Relation type</label>
                    <select
                      value={filterRelationId}
                      onChange={(e) => setFilterRelationId(e.target.value)}
                      style={{ width: '100%', padding: '6px' }}
                    >
                      <option value="">— select —</option>
                      {sortRelations(allRelations).map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '8px', position: 'relative' }}>
                    <label style={{ display: 'block', marginBottom: '4px' }}>
                      Target object <span style={{ color: 'var(--wv-muted)' }}>(leave blank for wildcard)</span>
                    </label>
                    <input
                      type="text"
                      value={filterTargetContent || filterTargetSearch}
                      onChange={(e) => {
                        setFilterTargetSearch(e.target.value);
                        setFilterTargetId('');
                        setFilterTargetContent('');
                      }}
                      placeholder="Search objects..."
                      style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                    />
                    {searchResults.length > 0 && !filterTargetId && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, border: '1px solid var(--wv-border)', backgroundColor: 'var(--wv-surface)', maxHeight: '120px', overflowY: 'auto', zIndex: 10 }}>
                        {searchResults.slice(0, 8).map((obj) => (
                          <div
                            key={obj.id}
                            onClick={() => { setFilterTargetId(obj.id); setFilterTargetContent(obj.content); setFilterTargetSearch(''); setSearchResults([]); }}
                            style={{ padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--wv-border)', fontSize: '12px' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--wv-surface2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--wv-surface)'; }}
                          >
                            <span style={{ color: 'var(--wv-muted)', marginRight: '4px' }}>[{obj.object_type}]</span>
                            {obj.content?.substring(0, 50)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="submit" disabled={!filterRelationId} style={{ fontSize: '12px' }}>Add filter</button>
                </form>
              )}

              {Object.keys(filterGroups).length === 0 ? (
                <p style={{ fontSize: '13px', color: 'var(--wv-muted)' }}>
                  No filters yet — type a tag above to build a feed of everything carrying it.
                </p>
              ) : (
                Object.entries(filterGroups).map(([relName, groupFilters]) => (
                  <div key={relName} style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--wv-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {relName}
                    </div>
                    {groupFilters.map((f) => (
                      <div key={f.id} style={rowStyle}>
                        <span>{f.target_content || <em style={{ color: 'var(--wv-muted)' }}>any</em>}</span>
                        {/* Rollup, per filter (migration 018). Offered only on a
                            tag target: the flag is inert elsewhere, because the
                            resolver requires both ends of the hierarchy edge to
                            be tags, and offering a control that provably does
                            nothing is worse than not offering it. */}
                        {f.target_type === 'tag' && (
                          <label
                            title="Also match objects carrying any narrower tag filed under this one"
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)', marginLeft: 'auto' }}
                          >
                            <input
                              type="checkbox"
                              checked={!!f.include_descendants}
                              disabled={!isOwner}
                              onChange={(e) => handleToggleRollup(f.id, e.target.checked)}
                            />
                            + subtags
                          </label>
                        )}
                        {isOwner && (
                          <button onClick={() => handleRemoveFilter(f.id)} style={removeBtnStyle}>×</button>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            {/* Includes + sharing */}
            <div>
              {(isOwner || compositions.length > 0) && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: 'var(--wv-fs-ui)' }}>
                    <Explain concept="viewset-filters">Includes viewsets</Explain>
                  </h3>
                  {compositions.map((c) => (
                    <div key={c.id} style={rowStyle}>
                      <Link to={`/?viewset=${c.child_viewset_id}`} className="wv-plainlink" style={{ color: 'var(--wv-accent)' }}>{c.child_name}</Link>
                      {isOwner && (
                        <button onClick={() => handleRemoveComposition(c.id)} style={removeBtnStyle}>×</button>
                      )}
                    </div>
                  ))}
                  {isOwner && availableViewsets.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) handleAddComposition(e.target.value); }}
                      style={{ width: '100%', padding: '6px', marginTop: '6px', fontSize: '12px' }}
                    >
                      <option value="">+ Add viewset...</option>
                      {availableViewsets.map((vs) => (
                        <option key={vs.id} value={vs.id}>{vs.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {isOwner && (
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: 'var(--wv-fs-ui)' }}>
                    <Explain concept="viewset-visibility">Sharing</Explain>
                  </h3>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={viewset.is_public} onChange={handleTogglePublic} />
                    Public (visible to everyone)
                  </label>
                  {access.map((a) => (
                    <div key={a.id} style={rowStyle}>
                      <span>
                        <span style={{ color: 'var(--wv-muted)', marginRight: '4px' }}>[{a.grantee_type}]</span>
                        {a.grantee_content}
                      </span>
                      <button onClick={() => handleRevokeAccess(a.id)} style={removeBtnStyle}>×</button>
                    </div>
                  ))}
                  <div style={{ position: 'relative', marginTop: '6px' }}>
                    <input
                      type="text"
                      value={granteeSearch}
                      onChange={(e) => setGranteeSearch(e.target.value)}
                      placeholder="Share with user or group..."
                      style={{ width: '100%', padding: '6px', boxSizing: 'border-box', fontSize: '12px' }}
                    />
                    {granteeResults.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, border: '1px solid var(--wv-border)', backgroundColor: 'var(--wv-surface)', maxHeight: '120px', overflowY: 'auto', zIndex: 10 }}>
                        {granteeResults.map((obj) => (
                          <div
                            key={obj.id}
                            onClick={() => handleGrantAccess(obj.id)}
                            style={{ padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--wv-border)', fontSize: '12px' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--wv-surface2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--wv-surface)'; }}
                          >
                            <span style={{ color: 'var(--wv-muted)', marginRight: '4px' }}>[{obj.object_type}]</span>
                            {obj.content}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Remove, at the foot of the panel and nowhere else. A destructive
              affordance on the pill row itself would sit one mis-click from
              switching views, and the ruling is one management surface. */}
          {isOwner && (
            <div style={{ borderTop: '1px solid var(--wv-border)', marginTop: 'var(--wv-sp-5)', paddingTop: 'var(--wv-sp-4)', display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-3)' }}>
              <button
                onClick={handleDelete}
                style={{
                  background: 'none',
                  border: '1px solid var(--wv-border)',
                  borderRadius: 'var(--wv-r-xs)',
                  color: 'var(--wv-neg)',
                  fontSize: 'var(--wv-fs-xs)',
                  padding: '3px 10px',
                  cursor: 'pointer',
                }}
              >
                remove this viewset
              </button>
              <span style={{ fontSize: 'var(--wv-fs-xs)', color: 'var(--wv-muted)' }}>
                the definition is kept — nothing in this app hard-deletes
              </span>
            </div>
          )}
        </div>
      )}

      {/* The rows are the PARENT's — see the header. What stays here is the
          one message the parent cannot write, because only this component
          knows whether an empty result means "no filters yet" or "filters
          that match nothing". */}
      {filters.length === 0 && compositions.length === 0 && (
        <p style={{ color: 'var(--wv-muted)', fontSize: 'var(--wv-fs-md)' }}>
          An empty viewset — open ⚙ edit and add a tag filter to populate it.
        </p>
      )}
    </div>
  );
}

export default ViewsetInline;
